import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { pm } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { theme } from '../../theme';

// state_group → display config
const S_META = {
  backlog:   { color: '#6B7280', bg: '#F3F4F6', label: 'Backlog',     dot: '#9CA3AF' },
  unstarted: { color: '#2563EB', bg: '#EFF6FF', label: 'To Do',        dot: '#3B82F6' },
  started:   { color: '#D97706', bg: '#FFF7ED', label: 'In Progress',  dot: '#F97316' },
  completed: { color: '#059669', bg: '#F0FDF4', label: 'Done',         dot: '#10B981' },
  cancelled: { color: '#DC2626', bg: '#FFF1F2', label: 'Cancelled',    dot: '#F43F5E' },
};

const P_CFG = {
  urgent:   { color: '#DC2626', bg: '#FFF1F2', icon: '🔴' },
  critical: { color: '#DC2626', bg: '#FFF1F2', icon: '🔴' },
  high:     { color: '#D97706', bg: '#FFF7ED', icon: '🟠' },
  medium:   { color: '#D97706', bg: '#FFFBEB', icon: '🟡' },
  low:      { color: '#059669', bg: '#F0FDF4', icon: '🟢' },
  none:     { color: '#6B7280', bg: '#F9FAFB', icon: '⚪' },
};

const FILTERS = [
  { key: 'all',       label: 'All' },
  { key: 'unstarted', label: 'To Do' },
  { key: 'started',   label: 'In Progress' },
  { key: 'completed', label: 'Done' },
  { key: 'cancelled', label: 'Cancelled' },
];

const PRIORITY_ORDER = { urgent: 0, critical: 0, high: 1, medium: 2, low: 3, none: 4 };

function daysDiff(dateStr) {
  if (!dateStr) return null;
  const diff = Math.ceil((new Date(dateStr) - new Date(new Date().toDateString())) / 86400000);
  return diff;
}

function DueBadge({ due_date, status }) {
  if (!due_date || status === 'completed' || status === 'cancelled') return null;
  const diff = daysDiff(due_date);
  const fmtDate = new Date(due_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  if (diff < 0)  return <span style={{ background: '#FFF1F2', color: '#DC2626', padding: '2px 8px', borderRadius: 9999, fontSize: '0.7rem', fontWeight: 700 }}>⚠ {Math.abs(diff)}d overdue</span>;
  if (diff === 0) return <span style={{ background: '#FFFBEB', color: '#B45309', padding: '2px 8px', borderRadius: 9999, fontSize: '0.7rem', fontWeight: 700 }}>⏰ Due today</span>;
  if (diff <= 3)  return <span style={{ background: '#FFF7ED', color: '#D97706', padding: '2px 8px', borderRadius: 9999, fontSize: '0.7rem', fontWeight: 600 }}>📅 {fmtDate}</span>;
  return             <span style={{ background: '#F3F4F6', color: '#6B7280', padding: '2px 8px', borderRadius: 9999, fontSize: '0.7rem' }}>📅 {fmtDate}</span>;
}

const PRIORITIES = ['none', 'low', 'medium', 'high', 'urgent'];
const EMPTY_FORM = { project_id: '', module_id: '', title: '', description: '', priority: 'medium', start_date: '', due_date: '' };

export default function MyTasks() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tasks, setTasks]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState('all');
  const [search, setSearch]   = useState('');
  const [sortBy, setSortBy]   = useState('due');   // due | priority | project

  // Add task modal
  const [showAdd, setShowAdd]   = useState(false);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [projects, setProjects] = useState([]);
  const [modules, setModules]   = useState([]);
  const [saving, setSaving]     = useState(false);
  const [addError, setAddError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await pm.getMyIssues(); setTasks(r.data || []); }
    catch { setTasks([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (showAdd && projects.length === 0) {
      pm.getProjects().then(r => setProjects(r.data || [])).catch(() => {});
    }
  }, [showAdd]); // eslint-disable-line

  useEffect(() => {
    setModules([]);
    setForm(f => ({ ...f, module_id: '' }));
    if (form.project_id) {
      pm.getModules(form.project_id).then(r => setModules(r.data || [])).catch(() => {});
    }
  }, [form.project_id]); // eslint-disable-line

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!form.project_id) { setAddError('Please select a project.'); return; }
    if (!form.title.trim()) { setAddError('Task title is required.'); return; }
    setSaving(true); setAddError('');
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        type: 'task',
        priority: form.priority,
        start_date: form.start_date || null,
        due_date: form.due_date || null,
        assignee_ids: user?.emp_id ? [user.emp_id] : [],
      };
      const res = await pm.createIssue(form.project_id, payload);
      const created = res.data;
      // Assign to module if selected
      if (form.module_id && created?.id) {
        await pm.addIssuesToModule(form.module_id, { issue_ids: [created.id] }).catch(() => {});
      }
      const taskCode = created?.code || created?.sequence_id ? `${created.code || ''}` : '';
      setSuccessMsg(`Task ${taskCode} created and assigned to you.`);
      setForm(EMPTY_FORM);
      setShowAdd(false);
      setTimeout(() => setSuccessMsg(''), 4000);
      load();
    } catch (err) {
      setAddError(err?.response?.data?.detail || 'Failed to create task.');
    } finally { setSaving(false); }
  };

  useEffect(() => { load(); }, [load]);

  const filtered = tasks
    .filter(t => filter === 'all' || t.status === filter)
    .filter(t => !search || t.title.toLowerCase().includes(search.toLowerCase()) ||
      (t.project_name || '').toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === 'priority') return (PRIORITY_ORDER[a.priority] ?? 4) - (PRIORITY_ORDER[b.priority] ?? 4);
      if (sortBy === 'project')  return (a.project_name || '').localeCompare(b.project_name || '');
      // due: overdue first, then soonest, then no date
      const da = a.due_date ? daysDiff(a.due_date) : 9999;
      const db_ = b.due_date ? daysDiff(b.due_date) : 9999;
      return da - db_;
    });

  const byProject = filtered.reduce((acc, t) => {
    const k = t.project_id || 'none';
    if (!acc[k]) acc[k] = { name: t.project_name || 'No Project', id: t.project_id, tasks: [] };
    acc[k].tasks.push(t);
    return acc;
  }, {});

  const total    = tasks.length;
  const inProg   = tasks.filter(t => t.status === 'started').length;
  const overdue  = tasks.filter(t => t.due_date && t.status !== 'completed' && t.status !== 'cancelled' && daysDiff(t.due_date) < 0).length;
  const done     = tasks.filter(t => t.status === 'completed').length;

  const filterCounts = {};
  tasks.forEach(t => { filterCounts[t.status] = (filterCounts[t.status] || 0) + 1; });

  return (
    <>
    <div style={{ fontFamily: "'Inter',-apple-system,sans-serif", display: 'flex', flexDirection: 'column', gap: '1.25rem', maxWidth: 1000, margin: '0 auto' }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .task-row:hover { background: #F8FAFC !important; }
      `}</style>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h1 style={{ fontSize: '1.4rem', fontWeight: 800, color: theme.text, margin: 0, letterSpacing: '-0.02em' }}>My Tasks</h1>
          <p style={{ fontSize: '0.82rem', color: theme.textMuted, margin: '0.2rem 0 0' }}>
            Tasks assigned to you across all projects
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.625rem' }}>
          <button onClick={() => { setShowAdd(true); setAddError(''); }} style={{ padding: '0.5rem 1.1rem', background: theme.primary, border: 'none', borderRadius: '0.5rem', fontSize: '0.82rem', fontWeight: 700, color: '#fff', cursor: 'pointer' }}>
            + Add Task
          </button>
          <button onClick={load} style={{ padding: '0.45rem 1rem', background: '#fff', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.82rem', fontWeight: 600, color: theme.textMuted, cursor: 'pointer' }}>
            ↻ Refresh
          </button>
        </div>
      </div>

      {successMsg && (
        <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '0.5rem', padding: '0.65rem 1rem', color: '#166534', fontSize: '0.82rem', fontWeight: 600 }}>
          ✓ {successMsg}
        </div>
      )}

      {/* ── Stats bar ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.875rem' }}>
        {[
          { label: 'Total Assigned', value: total,  color: '#3B82F6', bg: '#EFF6FF', icon: '📋' },
          { label: 'In Progress',    value: inProg,  color: '#F97316', bg: '#FFF7ED', icon: '⚡' },
          { label: 'Overdue',        value: overdue, color: '#EF4444', bg: '#FFF1F2', icon: '⚠️' },
          { label: 'Completed',      value: done,    color: '#10B981', bg: '#F0FDF4', icon: '✅' },
        ].map(s => (
          <div key={s.label} style={{ background: s.bg, border: `1px solid ${s.color}25`, borderRadius: '0.75rem', padding: '0.875rem 1rem', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 8, right: 10, fontSize: '1.4rem', opacity: 0.3 }}>{s.icon}</div>
            <p style={{ fontSize: '0.68rem', fontWeight: 700, color: s.color, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 0.25rem' }}>{s.label}</p>
            <p style={{ fontSize: '1.9rem', fontWeight: 800, color: s.color, margin: 0, lineHeight: 1 }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* ── Filters + Search + Sort ── */}
      <div style={{ background: '#fff', border: `1px solid ${theme.border}`, borderRadius: '0.75rem', padding: '0.875rem 1rem', display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
        {/* Status filter pills */}
        <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', flex: 1 }}>
          {FILTERS.map(f => {
            const count = f.key === 'all' ? total : (filterCounts[f.key] || 0);
            const active = filter === f.key;
            const sm = S_META[f.key];
            return (
              <button key={f.key} onClick={() => setFilter(f.key)} style={{
                padding: '0.3rem 0.75rem', borderRadius: '999px', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s',
                border: `1.5px solid ${active ? (sm?.color || theme.primary) : theme.border}`,
                background: active ? (sm?.bg || '#EFF6FF') : '#F9FAFB',
                color: active ? (sm?.color || theme.primary) : theme.textMuted,
                display: 'flex', alignItems: 'center', gap: '0.3rem',
              }}>
                {f.label}
                <span style={{ background: active ? (sm?.color || theme.primary) : '#E5E7EB', color: active ? '#fff' : theme.textMuted, borderRadius: '999px', padding: '0 5px', fontSize: '0.65rem', fontWeight: 700 }}>{count}</span>
              </button>
            );
          })}
        </div>

        {/* Search */}
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tasks or projects…"
          style={{ padding: '0.45rem 0.8rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.82rem', outline: 'none', width: 200, background: '#F9FAFB', color: theme.text }} />

        {/* Sort */}
        <select value={sortBy} onChange={e => setSortBy(e.target.value)}
          style={{ padding: '0.45rem 0.7rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.82rem', background: '#F9FAFB', color: theme.text, outline: 'none', cursor: 'pointer' }}>
          <option value="due">Sort: Due Date</option>
          <option value="priority">Sort: Priority</option>
          <option value="project">Sort: Project</option>
        </select>
      </div>

      {/* ── Loading ── */}
      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '3rem', gap: '0.75rem' }}>
          <div style={{ width: 28, height: 28, border: `3px solid ${theme.border}`, borderTop: `3px solid ${theme.primary}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          <span style={{ color: theme.textMuted, fontSize: '0.85rem' }}>Loading your tasks…</span>
        </div>
      )}

      {/* ── Empty state ── */}
      {!loading && Object.keys(byProject).length === 0 && (
        <div style={{ textAlign: 'center', padding: '4rem 2rem', background: '#F8FAFC', borderRadius: '1rem', border: `2px dashed ${theme.border}` }}>
          <div style={{ fontSize: '3rem', marginBottom: '0.75rem' }}>🎯</div>
          <h3 style={{ fontWeight: 700, color: theme.text, margin: '0 0 0.4rem' }}>
            {tasks.length === 0 ? 'No tasks assigned yet' : 'No tasks match this filter'}
          </h3>
          <p style={{ fontSize: '0.85rem', color: theme.textMuted, margin: 0 }}>
            {tasks.length === 0 ? 'Tasks assigned to you will appear here.' : 'Try changing the status filter or clearing the search.'}
          </p>
        </div>
      )}

      {/* ── Task groups ── */}
      {!loading && Object.values(byProject).map(group => (
        <div key={group.id || 'none'} style={{ background: '#fff', border: `1px solid ${theme.border}`, borderRadius: '0.875rem', overflow: 'hidden', boxShadow: '0 1px 6px rgba(0,0,0,0.04)' }}>

          {/* Project header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1.1rem', background: '#F8FAFC', borderBottom: `1px solid ${theme.border}` }}>
            <button onClick={() => group.id && navigate(`/projects/${group.id}`)}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'none', border: 'none', cursor: group.id ? 'pointer' : 'default', padding: 0 }}>
              <span style={{ fontSize: '1rem' }}>🗂</span>
              <span style={{ fontSize: '0.82rem', fontWeight: 800, color: theme.primary, textTransform: 'uppercase', letterSpacing: '0.07em' }}>{group.name}</span>
              {group.id && <span style={{ fontSize: '0.7rem', color: theme.textMuted }}>↗</span>}
            </button>
            <span style={{ background: theme.primary + '15', color: theme.primary, borderRadius: '999px', padding: '2px 10px', fontSize: '0.72rem', fontWeight: 700 }}>
              {group.tasks.length} task{group.tasks.length !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Task rows */}
          {group.tasks.map((task, idx) => {
            const sm     = S_META[task.status] || S_META.unstarted;
            const pc     = P_CFG[task.priority] || P_CFG.none;
            const stateColor = task.state_color || sm.dot;
            const stateBg    = sm.bg;
            const stateLabel = task.state_name  || sm.label;
            const isLast = idx === group.tasks.length - 1;

            return (
              <div key={task.id} className="task-row"
                onClick={() => group.id && navigate(`/projects/${group.id}`)}
                style={{ display: 'flex', alignItems: 'center', gap: '0.875rem', padding: '0.75rem 1.1rem', borderBottom: isLast ? 'none' : `1px solid #F1F5F9`, cursor: group.id ? 'pointer' : 'default', transition: 'background 0.12s' }}>

                {/* Priority stripe */}
                <div style={{ width: 4, height: 36, borderRadius: 2, background: pc.color, flexShrink: 0, opacity: 0.7 }} />

                {/* Status dot */}
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: stateColor, flexShrink: 0, boxShadow: `0 0 0 3px ${stateColor}25` }} />

                {/* Title + story */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{
                    margin: 0, fontSize: '0.875rem', fontWeight: 600,
                    color: task.status === 'completed' ? theme.textMuted : theme.text,
                    textDecoration: task.status === 'completed' ? 'line-through' : 'none',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>{task.title}</p>
                  {task.story_title && (
                    <p style={{ margin: '0.15rem 0 0', fontSize: '0.7rem', color: theme.textMuted }}>
                      ◈ {task.story_title}
                    </p>
                  )}
                </div>

                {/* Badges row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {/* Priority */}
                  <span style={{ background: pc.bg, color: pc.color, padding: '2px 8px', borderRadius: 9999, fontSize: '0.7rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 3 }}>
                    {pc.icon} {task.priority || 'none'}
                  </span>

                  {/* Status */}
                  <span style={{ background: stateBg, color: stateColor, padding: '2px 8px', borderRadius: 9999, fontSize: '0.7rem', fontWeight: 600 }}>
                    {stateLabel}
                  </span>

                  {/* Subtasks */}
                  {task.subtask_count > 0 && (
                    <span style={{ background: '#EDE9FE', color: '#6D28D9', padding: '2px 8px', borderRadius: 9999, fontSize: '0.7rem', fontWeight: 600 }}>
                      ⊡ {task.done_subtask_count}/{task.subtask_count}
                    </span>
                  )}

                  {/* Due date */}
                  <DueBadge due_date={task.due_date} status={task.status} />
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>

      {/* ── Add Task Modal ── */}
      {showAdd && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
          onClick={e => { if (e.target === e.currentTarget) setShowAdd(false); }}>
          <div style={{ background: '#fff', borderRadius: '0.875rem', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', width: '100%', maxWidth: 520, overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.5rem', borderBottom: `1px solid ${theme.border}` }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: theme.text }}>Add New Task</h2>
                <p style={{ margin: '0.15rem 0 0', fontSize: '0.75rem', color: theme.textMuted }}>Task ID auto-generated · Auto-assigned to you</p>
              </div>
              <button onClick={() => setShowAdd(false)} style={{ background: 'none', border: 'none', fontSize: '1.25rem', color: theme.textMuted, cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>

            {/* Form */}
            <form onSubmit={handleAddSubmit}>
              <div style={{ padding: '1.25rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>

                {addError && (
                  <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '0.5rem', padding: '0.6rem 0.875rem', color: '#DC2626', fontSize: '0.8rem' }}>
                    {addError}
                  </div>
                )}

                {/* Project */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.35rem' }}>Project *</label>
                  <select required value={form.project_id} onChange={e => setForm(f => ({ ...f, project_id: e.target.value }))}
                    style={{ width: '100%', padding: '0.55rem 0.75rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, background: '#fff', outline: 'none', boxSizing: 'border-box' }}>
                    <option value="">— Select project —</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.name} ({p.code})</option>)}
                  </select>
                </div>

                {/* Module (optional) */}
                {modules.length > 0 && (
                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.35rem' }}>Module <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span></label>
                    <select value={form.module_id} onChange={e => setForm(f => ({ ...f, module_id: e.target.value }))}
                      style={{ width: '100%', padding: '0.55rem 0.75rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, background: '#fff', outline: 'none', boxSizing: 'border-box' }}>
                      <option value="">— No module —</option>
                      {modules.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </select>
                  </div>
                )}

                {/* Title */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.35rem' }}>Task Title *</label>
                  <input required value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                    placeholder="What needs to be done?"
                    style={{ width: '100%', padding: '0.55rem 0.75rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none', boxSizing: 'border-box' }} />
                </div>

                {/* Description */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.35rem' }}>Description <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span></label>
                  <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                    rows={3} placeholder="Add more details…"
                    style={{ width: '100%', padding: '0.55rem 0.75rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, resize: 'vertical', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }} />
                </div>

                {/* Priority + Dates row */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.35rem' }}>Priority</label>
                    <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
                      style={{ width: '100%', padding: '0.55rem 0.6rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.82rem', color: theme.text, background: '#fff', outline: 'none', boxSizing: 'border-box' }}>
                      {PRIORITIES.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.35rem' }}>Start Date</label>
                    <input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
                      style={{ width: '100%', padding: '0.5rem 0.6rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.82rem', color: theme.text, outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.35rem' }}>Est. End Date</label>
                    <input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))}
                      min={form.start_date || undefined}
                      style={{ width: '100%', padding: '0.5rem 0.6rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.82rem', color: theme.text, outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                </div>

                {/* Auto-assign notice */}
                <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '0.5rem', padding: '0.5rem 0.75rem', fontSize: '0.75rem', color: '#166534', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  ✓ Task ID will be auto-generated · Automatically assigned to <strong>{user?.full_name || 'you'}</strong>
                </div>
              </div>

              {/* Footer */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', padding: '0.875rem 1.5rem', borderTop: `1px solid ${theme.border}`, background: '#FAFAFA' }}>
                <button type="button" onClick={() => setShowAdd(false)}
                  style={{ padding: '0.5rem 1.1rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', background: '#fff', fontSize: '0.82rem', fontWeight: 600, color: theme.text, cursor: 'pointer' }}>
                  Cancel
                </button>
                <button type="submit" disabled={saving}
                  style={{ padding: '0.5rem 1.25rem', border: 'none', borderRadius: '0.5rem', background: saving ? '#9CA3AF' : theme.primary, color: '#fff', fontSize: '0.82rem', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer' }}>
                  {saving ? 'Creating…' : 'Create Task'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
